import React from 'react';
import { useApp } from '@/contexts/AppContext';
import { t } from '@/locales/strings';

const styles = {
  card: {
    position: 'relative',
    overflow: 'hidden',
    margin: '0 0 16px',
    borderRadius: 22,
    padding: '26px 22px 24px',
    background:
      'radial-gradient(120% 80% at 84% 18%, rgba(255,255,255,0.16), transparent 55%), ' +
      'linear-gradient(142deg, var(--primary) 0%, var(--primary-dark) 68%, #6B0028 100%)',
    boxShadow:
      '0 1px 2px rgba(45, 26, 31, 0.06), 0 22px 40px -24px rgba(168, 0, 63, 0.45)',
    color: '#ffffff',
  },
  rings: {
    position: 'absolute',
    right: -36,
    bottom: -40,
    width: 160,
    height: 160,
    pointerEvents: 'none',
    opacity: 0.28,
  },
  eyebrow: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    fontFamily: 'var(--font-ui)',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.24em',
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.72)',
    marginBottom: 10,
  },
  eyebrowDot: {
    display: 'inline-block',
    width: 5,
    height: 5,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.9)',
  },
  title: {
    position: 'relative',
    fontFamily: 'var(--font-title)',
    fontStyle: 'italic',
    fontWeight: 500,
    fontSize: 32,
    color: '#ffffff',
    margin: '0 0 10px',
    lineHeight: 1.08,
    letterSpacing: '-0.02em',
    fontVariationSettings: '"opsz" 96',
  },
  body: {
    position: 'relative',
    fontFamily: 'var(--font-body)',
    fontSize: 15,
    color: 'rgba(255,255,255,0.84)',
    lineHeight: 1.65,
    margin: 0,
    maxWidth: 340,
  },
};

/**
 * Year 1 "new movement" callout.
 *
 * Appears above the session card on the specific day a new named
 * movement is introduced. Distinct from Year 2's weekly inline
 * NewMovementCard (defined locally in WeekHome.jsx).
 *
 * WeekHome is responsible for:
 *   1. Looking up the current movement via getNewMovementForDay()
 *   2. Gating the render on isYear1 && newMovement
 *   3. Placing this component inside the session-card section wrapper,
 *      above the REST/card ternary.
 *
 * Visual intent: a deep-rose editorial plaque, not a button. Layered
 * gradient so the surface reads as lit, decorative concentric rings
 * on the right (same motif as the session card + loader), soft
 * shine sweep on first mount.
 */
export default function Year1NewMovementCard({ movement }) {
  const { lang } = useApp();
  if (!movement) return null;

  const name = lang === 'pt' ? movement.name_pt : movement.name;
  const description = lang === 'pt' ? movement.description_pt : movement.description;

  return (
    <div className="motion-shine" style={styles.card}>
      {/* Decorative three-ring motif — matches the session card and
          the brand 3-ring loader. Absolute-positioned, aria-hidden. */}
      <svg viewBox="0 0 160 160" aria-hidden="true" style={styles.rings}>
        <ellipse cx="80" cy="80" rx="66" ry="46" stroke="rgba(255,255,255,0.85)" strokeWidth="1" fill="none" />
        <ellipse cx="80" cy="80" rx="46" ry="32" stroke="rgba(255,255,255,0.68)" strokeWidth="1" fill="none" />
        <ellipse cx="80" cy="80" rx="26" ry="18" stroke="rgba(255,255,255,0.55)" strokeWidth="1" fill="none" />
        <circle cx="80" cy="80" r="2" fill="rgba(255,255,255,0.85)" />
      </svg>

      <span style={styles.eyebrow}>
        <span aria-hidden="true" style={styles.eyebrowDot} />
        {t('new_movement_label', lang)}
      </span>

      <h3 style={styles.title}>{name}</h3>

      <p style={styles.body}>{description}</p>
    </div>
  );
}

